<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ChevronRight, Bell, Moon, Sun } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { useAppStore } from '@/stores/app.store';
import { storeToRefs } from 'pinia';

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const { theme } = storeToRefs(appStore);

interface Crumb {
	label: string;
	path?: string;
}

// Derive breadcrumbs generically from the matched route records so this works
// for every dashboard-shell section (dashboard, mantle, 125data, user) without
// a hand-maintained path map.
const crumbs = computed<Crumb[]>(() => {
	const matched = route.matched
		.map((record) => {
			const label =
				(record.meta?.breadcrumb as string) || (record.meta?.title as string) || '';
			return {
				label,
				// Parameterised paths can't be navigated to directly.
				path: record.path.includes(':') ? undefined : record.path,
			};
		})
		.filter((crumb) => {
			return !!crumb.label;
		});

	return matched.length ? matched : [{ label: '125 Insights' }];
});

const handleCrumb = (path?: string) => {
	if (path) {
		router.push(path);
	}
};

const toggleTheme = () => {
	return appStore.toggleTheme();
};
</script>

<template>
	<header
		class="ds-topbar sticky top-0 z-30 flex h-14 items-center gap-3.5 border-b border-[color:var(--depot-border)] px-8 max-md:px-4"
	>
		<nav
			class="flex items-center gap-2 text-[13px] text-[color:var(--fg-faint)]"
			aria-label="Breadcrumb"
		>
			<template v-for="(c, i) in crumbs" :key="i">
				<ChevronRight v-if="i > 0" :size="12" :stroke-width="1.4" />
				<span
					:class="
						i === crumbs.length - 1
							? 'text-[color:var(--fg)]'
							: c.path
								? 'cursor-pointer hover:text-[color:var(--fg)]'
								: ''
					"
					@click="i === crumbs.length - 1 ? null : handleCrumb(c.path)"
					>{{ c.label }}</span
				>
			</template>
		</nav>
		<div class="flex-1" />
		<div class="flex items-center gap-1">
			<!-- <Button
				variant="ghost"
				size="icon-sm"
				class="relative"
				type="button"
				title="Notifications"
			>
				<Bell :size="14" :stroke-width="1.6" />
				<span
					class="absolute right-[6px] top-[6px] size-1.5 rounded-full bg-[color:var(--depot-accent)] shadow-[0_0_0_2px_var(--bg)]"
				/>
			</Button> -->
			<Button
				variant="ghost"
				size="icon-sm"
				type="button"
				title="Toggle theme"
				@click="toggleTheme"
			>
				<Sun v-if="theme === 'dark'" :size="14" :stroke-width="1.6" />
				<Moon v-else :size="14" :stroke-width="1.6" />
			</Button>
		</div>
	</header>
</template>
